import { useEffect, useState } from "react"
import { onAuthStateChanged, signInWithCustomToken } from "firebase/auth"
import { getAuth, signOut } from "firebase/auth"
import { doc, getDoc, getFirestore, setDoc } from "firebase/firestore"
import axios from "axios"
import MESSAGE from "lang/messages"
import localforage from "localforage"

type Login = { email: string; password: string }
type OnAuthFunc = (inspector: Inspector | null) => void
export type Inspector = {
  uid: string
  fname: string
  lname: string
  role: string
  verified: boolean
  email: string
  phone: string
  token: string
  signature: string
  licence_number: string
  fullBodyPhoto: string
  headshot: string
  profilePhoto: string
  spreadsheet: string
  pest_control_license_number: string
  agreement_type: string
}

export async function getAuthHeader() {
  const auth = getAuth()
  const user = auth.currentUser
  if (!user) return {}
  const token = await user.getIdToken()
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  }
}
export function login(data: Login): Promise<Inspector | null> {
  return new Promise(async (res, rej) => {
    const config = {
      method: "post",
      url: `${process.env.GATSBY_API_ENDPOINT}/auth`,
      data,
      headers: {
        "Content-Type": "application/json",
      },
    }

    try {
      const response = await axios(config)

      if (response.status !== 200) return rej("login failed")
      const inspector = response.data as Inspector
      const docPath = `inspectors/${inspector.uid}`
      const document = doc(getFirestore(), docPath)
      await signInWithCustomToken(getAuth(), inspector.token)

      inspector.uid = inspector.uid.toString()
      setDoc(document, inspector, { merge: true })
      localforage.setItem("inspector", inspector)

      res(inspector)
    } catch (error) {
      rej(error)
    }
  })
}
export async function loginOut(fn?: () => void) {
  await signOut(getAuth())
  localStorage.clear()
  localforage.clear()
  if (fn) fn()
  return
}
export function onAuthState(cb: OnAuthFunc) {
  return onAuthStateChanged(getAuth(), async user => {
    const inspector = await localforage.getItem("inspector")
    if (!user) return cb(null)

    if (inspector) cb(inspector as Inspector)

    const document = doc(getFirestore(), `inspectors/${user.uid}`)
    // const snapshot = await getDoc(document)
    getDoc(document)
      .then(snapshot => {
        localforage.setItem("inspector", snapshot.data())
        if (!inspector) cb(snapshot.data() as Inspector)
      })
      .catch(err => { })
  })
}
export function getUid() {
  if (typeof window !== "undefined") {
    return localStorage.getItem("uid") as string
  }
  return ""
}
export function getUser(uid?: string): Promise<Inspector> {
  return new Promise(async (res, rej) => {
    onAuthState(async inspector => {
      if (!inspector) {
        rej(new Error(MESSAGE.unauthenticated))
        return null
      }
      if (!uid) return res(inspector)
      const snapshot = await getDoc(doc(getFirestore(), `inspectors/${uid}`))
      return res(snapshot.data() as Inspector)
    })
  })
}
export async function useGetInspector(uid: string) {
  const snapshot = await getDoc(doc(getFirestore(), `inspectors/${uid}`))
  return snapshot.data() as Inspector
}

export function useAuth(cb?: () => void) {
  const [authenticated, setAuthenticated] = useState<Inspector | null>(null)
  useEffect(() => {
    function onAuthStateChanged(user: Inspector | null) {
      if (cb) cb()
      if (authenticated && user) return
      setAuthenticated(user)
    }
    const unsubscribe = onAuthState(onAuthStateChanged)
    return () => unsubscribe()
  }, [])
  return authenticated
}
